import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, BadgeIndianRupee, Building2, CreditCard,
  CheckCircle2, Loader2, LockKeyhole, ShieldCheck, Sparkles, WalletCards, XCircle
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const CREATE_PAYMENT_ENDPOINT = "/backend/api/payumoney/create_payment.php";
const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

async function readJson(response) {
  const text = await response.text();
  if (!text.trim()) throw new Error(`Server returned an empty response. HTTP ${response.status}.`);
  try {
    return JSON.parse(text);
  } catch {
    console.error("Invalid PayU response:", text);
    throw new Error("Server returned an invalid response. Check the PHP error log.");
  }
}

export default function PayUMoney() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const callbackStatus = searchParams.get("status");
  const paymentId = searchParams.get("payment_id") || "";
  const txnid = searchParams.get("txnid") || "";
  const paymentFinished = callbackStatus === "success" || callbackStatus === "failed";
  const paymentSuccess = callbackStatus === "success";

  const numericAmount = Number(amount);
  const validAmount = Number.isFinite(numericAmount) && numericAmount >= 1;
  const formattedAmount = useMemo(() => {
    if (!validAmount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR", maximumFractionDigits: 2
    }).format(numericAmount);
  }, [numericAmount, validAmount]);

  useEffect(() => {
    if (!paymentSuccess) return;
    const timer = setTimeout(() => {
      navigate("/dashboard", { replace: true });
    }, 2500);
    return () => clearTimeout(timer);
  }, [paymentSuccess, navigate]);

  if (paymentFinished) {
    return (
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="mx-auto max-w-xl">
          <Card className="rounded-3xl border">
            <CardContent className="p-8 text-center">
              {paymentSuccess ? (
                <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" />
              ) : (
                <XCircle className="mx-auto h-16 w-16 text-red-600" />
              )}

              <h1 className="mt-5 text-2xl font-bold">
                {paymentSuccess ? "Wallet Updated" : "Payment Not Completed"}
              </h1>

              <p className="mt-2 text-muted-foreground">
                {paymentSuccess
                  ? "Your PayU payment was verified and your wallet was credited. Redirecting to dashboard..."
                  : "Your wallet has not been credited. Please try again."}
              </p>

              {(paymentId || txnid) && (
                <div className="mt-5 rounded-2xl border bg-muted/30 p-4 text-left text-xs">
                  {paymentId && <p><b>PayU Payment ID:</b> {paymentId}</p>}
                  {txnid && <p className="mt-1"><b>Transaction ID:</b> {txnid}</p>}
                </div>
              )}

              <Button
                className="mt-6 w-full"
                onClick={() =>
                  navigate(paymentSuccess ? "/dashboard" : "/payumoney", {
                    replace: true,
                  })
                }
              >
                {paymentSuccess ? "Go to Dashboard" : "Try Again"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const selectAmount = (value) => {
    if (!loading) setAmount(String(value));
  };

  const handleAmountChange = (event) => {
    const value = event.target.value;
    if (value === "" || /^\d*(\.\d{0,2})?$/.test(value)) setAmount(value);
  };

  const startPayment = async () => {
    if (loading) return;
    if (!validAmount) {
      toast.error("Please enter an amount of at least ₹1.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(CREATE_PAYMENT_ENDPOINT, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ amount: numericAmount }),
      });

      const result = await readJson(response);

      if (response.status === 401) {
        toast.error(result.message || "Please login again.");
        navigate("/login", { replace: true });
        return;
      }

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to start PayU payment.");
      }

      // PayU Hosted Checkout requires a POST, not a React GET redirect.
      const form = document.createElement("form");
      form.method = "POST";
      form.action = result.payment_url;
      form.style.display = "none";

      Object.entries(result.fields || {}).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = String(value ?? "");
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("PayU payment error:", error);
      toast.error(error instanceof Error ? error.message : "Unable to start payment.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-6 sm:px-6 lg:py-10">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <LockKeyhole className="h-4 w-4" /> Secure Payment
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <Card className="overflow-hidden rounded-3xl border">
            <CardHeader className="border-b p-6 sm:p-8">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <WalletCards className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-3xl">Add Money with PayU</CardTitle>
              <p className="text-sm text-muted-foreground sm:text-base">
                Add funds securely using PayU Hosted Checkout.
              </p>
            </CardHeader>

            <CardContent className="space-y-7 p-6 sm:p-8">
              <div>
                <Label htmlFor="walletAmount">Amount</Label>
                <div className="relative mt-2">
                  <BadgeIndianRupee className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="walletAmount"
                    value={amount}
                    onChange={handleAmountChange}
                    inputMode="decimal"
                    placeholder="Enter amount"
                    disabled={loading}
                    className="h-14 pl-12 text-xl font-semibold"
                    autoComplete="off"
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Minimum wallet top-up: ₹1</p>
              </div>

              <div>
                <p className="mb-3 text-sm font-medium">Quick Amount</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {QUICK_AMOUNTS.map((value) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={Number(amount) === value ? "default" : "outline"}
                      onClick={() => selectAmount(value)}
                      disabled={loading}
                      className="h-10"
                    >
                      ₹{value}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border bg-muted/20 p-5">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground">Wallet Top-up</span>
                  <span className="font-semibold">{formattedAmount}</span>
                </div>
                <div className="my-4 border-t" />
                <div className="flex items-center justify-between gap-4">
                  <span className="font-medium">Payable Amount</span>
                  <span className="text-xl font-bold">{formattedAmount}</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={startPayment}
                disabled={loading || !validAmount}
                className="h-12 w-full text-base font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Redirecting to PayU...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Pay {validAmount ? formattedAmount : ""} with PayU
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="rounded-3xl border">
              <CardContent className="p-6 sm:p-7">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-primary/10 p-3">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold">Secure Wallet Credit</h2>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      Your server creates the PayU hash and verifies the transaction before wallet credit.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border">
              <CardContent className="p-6 sm:p-7">
                <div className="mb-5 flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  <h2 className="font-semibold">Payment Process</h2>
                </div>
                <div className="space-y-5">
                  <Step number="1" title="Enter amount" text="Choose a quick amount or enter a custom amount." />
                  <Step number="2" title="Pay securely" text="Continue to PayU Hosted Checkout." />
                  <Step number="3" title="Server verification" text="PHP verifies the PayU transaction." />
                  <Step number="4" title="Wallet updated" text="Only a verified successful payment is credited." />
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-3xl border">
              <CardContent className="flex items-center gap-4 p-6 sm:p-7">
                <Building2 className="h-6 w-6 text-primary" />
                <p className="text-sm text-muted-foreground">
                  Merchant Key and Salt remain on your PHP server and are never sent to React.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ number, title, text }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted text-sm font-semibold">
        {number}
      </div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-sm leading-5 text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}
